/**
 * 章节正文解密 (AES-GCM)
 *
 * 后端下发的章节文件格式为: Base64( IV(12 bytes) + CipherText + AuthTag(16 bytes) )
 *
 * 采用纯 JS 实现 (@noble/ciphers), 不依赖 Web Crypto 的 `crypto.subtle`, 因此:
 * - 无安全上下文限制: `http://内网IP:端口` 直接调试、微信内置浏览器均可解密,
 *   不需要 `pnpm dev:https`, 也不需要向设备分发本地证书
 * - 与小程序端 (book2) 使用同一套密钥与密文格式, 多端解密行为一致
 *
 * 密钥为明文字符串, 实际使用其 UTF-8 字节 (32 bytes -> AES-256);
 * 同时兼容 Base64 解码 (24 bytes -> AES-192) 的实现, 逐个尝试。
 * 密钥可通过 `VITE_CHAPTER_AES_KEY` 覆盖, 缺省使用内置默认密钥。
 */

import { gcm } from '@noble/ciphers/aes.js'
import * as Sentry from '@sentry/vue'

const IV_LENGTH = 12
/** GCM AuthTag 长度 (字节), 拼接在密文体末尾 */
const TAG_LENGTH = 16
/** IV(12) + AuthTag(16) = 28 bytes, 密文体的最小长度 */
const MIN_CIPHER_BYTES = IV_LENGTH + TAG_LENGTH
/** 28 bytes 密文对应的 Base64 最小长度 (无 padding) */
const MIN_CIPHER_BASE64_LENGTH = 38

const DEFAULT_CHAPTER_AES_KEY = 'UlBOFsvU06p4C0gJXk7t491ZPszwDIgm'
const CHAPTER_AES_KEY = import.meta.env.VITE_CHAPTER_AES_KEY || DEFAULT_CHAPTER_AES_KEY

/** 标准 / URL 安全的 Base64, 允许 0~2 个 padding */
const BASE64_PATTERN = /^[A-Za-z0-9+/\-_]+={0,2}$/

/** 密钥候选只与密钥内容有关, 缓存以避免每章重复推导 */
let cachedKeyMaterials: Uint8Array[] | null = null
let cachedKeySource = ''

/**
 * Base64 -> Uint8Array, 兼容 URL 安全字符与缺失的 padding
 */
function base64ToBytes(base64: string) {
  const normalized = base64
    .replace(/\s/g, '')
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const padded = normalized + '='.repeat((4 - (normalized.length % 4)) % 4)
  const raw = atob(padded)
  const bytes = new Uint8Array(raw.length)
  for (let i = 0; i < raw.length; i++)
    bytes[i] = raw.charCodeAt(i)

  return bytes
}

/**
 * 密钥候选: 优先密钥字符串的 UTF-8 字节, 兜底尝试 Base64 解码后的字节
 *
 * Note: 纯 JS 实现支持 AES-128/192/256, 不再受 WebKit 不提供 AES-192 的限制。
 */
function getKeyMaterials() {
  if (!cachedKeyMaterials || cachedKeySource !== CHAPTER_AES_KEY) {
    const materials: Uint8Array[] = [new TextEncoder().encode(CHAPTER_AES_KEY)]
    try {
      const decoded = base64ToBytes(CHAPTER_AES_KEY)
      if (decoded.length !== materials[0].length)
        materials.push(decoded)
    }
    catch {
      // Note: 密钥不是合法 Base64, 仅使用 UTF-8 字节
    }
    cachedKeySource = CHAPTER_AES_KEY
    cachedKeyMaterials = materials
  }
  return cachedKeyMaterials
}

/**
 * 判断文本是否为加密章节内容
 *
 * 明文正文必然包含换行 / 空格 / 中文等字符, 不会命中纯 Base64 字符集,
 * 因此可以据此兼容灰度期间未加密的存量内容。
 */
export function isCipherText(text: string) {
  const value = text.trim()
  return value.length >= MIN_CIPHER_BASE64_LENGTH && BASE64_PATTERN.test(value)
}

/**
 * 解密 AES-GCM 章节内容
 *
 * @param cipherTextBase64 Base64( IV + CipherText + AuthTag )
 * @throws 密文非法 / 密钥不匹配时抛出
 */
export async function decryptAESGCM(cipherTextBase64: string) {
  const data = base64ToBytes(cipherTextBase64)
  if (data.length < MIN_CIPHER_BYTES)
    throw new Error('章节密文长度不合法')

  const iv = data.subarray(0, IV_LENGTH)
  // Note: 末尾 16 字节 AuthTag 留在 payload 内, 由 gcm 解密时自动剥离并校验
  const payload = data.subarray(IV_LENGTH)

  let lastError: unknown
  for (const key of getKeyMaterials()) {
    try {
      return new TextDecoder().decode(gcm(key, iv).decrypt(payload))
    }
    catch (error) {
      lastError = error
    }
  }

  throw lastError instanceof Error ? lastError : new Error('章节内容解密失败')
}

/**
 * 解析章节文本: 密文解密, 明文原样返回
 *
 * 解密失败时返回空串并上报, 避免把密文原文直接展示给用户。
 */
export async function decodeChapterText(text: string) {
  if (!text || !isCipherText(text))
    return text

  try {
    return await decryptAESGCM(text)
  }
  catch (error) {
    console.error('[crypto] 章节内容解密失败', error)
    if (import.meta.env.PROD && Sentry.getClient())
      Sentry.captureException(error)

    return ''
  }
}
